/**
 * Shared helpers for debounced list search + infinite-scroll pagination.
 *
 * Prevents races where a 1-character (partial) query remounts InfiniteScroll
 * against a stale `nextPage` URL, and where AbortController cancels are
 * treated as hard fetch failures.
 *
 * Note: intentionally avoids importing axios so CRA/Jest can load this util
 * without ESM transform issues on axios's package entry.
 */

/** Minimum characters before a text search is sent to the API. */
export const MIN_LIST_SEARCH_CHARS = 2;

/**
 * Distance from the bottom (px) at which InfiniteScroll should call loadMore.
 * Higher than the library default / prior 250 so the next page starts while
 * the user still has rows left to scroll (masks ~1s network latency).
 */
export const INFINITE_SCROLL_THRESHOLD_PX = 1200;

/**
 * Normalize a filter/search param for URL ↔ state comparisons.
 *
 * @param {*} value
 * @returns {string}
 */
export function normalizeListSearchParam(value) {
  return String(value ?? '').trim();
}

/**
 * True when the query should trigger a list fetch (empty = clear search, or 2+ chars).
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isRunnableListSearchQuery(value) {
  const len = normalizeListSearchParam(value).length;
  return len === 0 || len >= MIN_LIST_SEARCH_CHARS;
}

/**
 * True when the user has typed exactly one character — UI may clear results
 * but must not fetch or paginate with a stale next-page URL.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isPartialListSearchQuery(value) {
  return normalizeListSearchParam(value).length === 1;
}

/**
 * True when Axios aborted via AbortController / signal (or legacy cancel).
 *
 * @param {*} error
 * @returns {boolean}
 */
export function isCanceledRequest(error) {
  return Boolean(
    error?.__CANCEL__ ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError' ||
      (typeof error?.message === 'string' &&
        /^canceled$/i.test(error.message.trim()))
  );
}

/**
 * Abort the current list-search controller if present.
 * Prefer AbortController.abort(); also supports mistaken .cancel() refs.
 *
 * @param {{ current?: { abort?: Function, cancel?: Function } | null }} controllerRef
 */
export function abortListSearchController(controllerRef) {
  const controller = controllerRef?.current;
  if (!controller) return;
  if (typeof controller.abort === 'function') {
    controller.abort();
  } else if (typeof controller.cancel === 'function') {
    controller.cancel();
  }
}

/**
 * Reset pagination when the user enters a partial (1-char) search that must
 * not drive InfiniteScroll loadMore against a previous next-page URL.
 *
 * @param {Object} options
 * @param {Function} [options.setHasMore]
 * @param {{ current: boolean }} [options.hasMoreRef]
 * @param {Function} [options.setNextPageUrl]
 * @param {{ current: * }} [options.nextPageUrlRef]
 * @param {{ current?: * }} [options.abortControllerRef]
 */
export function resetPaginationForPartialSearch({
  setHasMore,
  hasMoreRef,
  setNextPageUrl,
  nextPageUrlRef,
  abortControllerRef,
} = {}) {
  if (typeof setHasMore === 'function') {
    setHasMore(false);
  }
  if (hasMoreRef) {
    hasMoreRef.current = false;
  }
  if (typeof setNextPageUrl === 'function') {
    setNextPageUrl(null);
  }
  if (nextPageUrlRef) {
    nextPageUrlRef.current = null;
  }
  if (abortControllerRef) {
    abortListSearchController(abortControllerRef);
  }
}

/**
 * InfiniteScroll `initialLoad` calls loadMore with no next URL yet.
 * Return true when we should fetch page 1 (empty list, no nextPageUrl).
 * Pagination must still require a real nextPageUrl.
 *
 * @param {{ nextPageUrl?: string|null, itemCount?: number }} [options]
 * @returns {boolean}
 */
export function shouldBootstrapInfiniteScrollLoad({
  nextPageUrl = null,
  itemCount = 0,
} = {}) {
  return !nextPageUrl && Number(itemCount) === 0;
}

/**
 * Compare selected query params on a next-page URL to expected UI filter values.
 *
 * @param {string|null|undefined} nextPageUrl
 * @param {Record<string, string>} expected - map of paramName → expected value (normalized)
 * @returns {{ mismatched: boolean, expected: Record<string, string>, actual: Record<string, string> } | null}
 *   null when URL cannot be parsed
 */
export function getNextPageUrlFilterMismatch(nextPageUrl, expected = {}) {
  if (!nextPageUrl) {
    return {
      mismatched: false,
      expected: {},
      actual: {},
    };
  }
  try {
    const url = new URL(nextPageUrl);
    const actual = {};
    const normalizedExpected = {};
    let mismatched = false;
    Object.keys(expected).forEach(paramName => {
      const exp = normalizeListSearchParam(expected[paramName]);
      const act = normalizeListSearchParam(url.searchParams.get(paramName));
      normalizedExpected[paramName] = exp;
      actual[paramName] = act;
      if (exp !== act) {
        mismatched = true;
      }
    });
    return { mismatched, expected: normalizedExpected, actual };
  } catch {
    return null;
  }
}
